import Phaser from 'phaser';

interface FocusEntry {
  activate: () => void;
  setFocused: (focused: boolean) => void;
  isActive: () => boolean;
}

export class FocusManager {
  static readonly instance = new FocusManager();
  private entries = new WeakMap<Phaser.Scene, FocusEntry[]>();
  private indices = new WeakMap<Phaser.Scene, number>();
  private installed = new WeakSet<Phaser.Scene>();
  private gamepadLatch = new WeakMap<Phaser.Scene, boolean>();

  register(scene: Phaser.Scene, entry: FocusEntry) {
    const list = this.activeEntries(scene);
    list.push(entry);
    this.entries.set(scene, list);
    if (!this.installed.has(scene)) this.install(scene);
    if (list.length === 1) this.focus(scene, 0);
  }

  clear(scene: Phaser.Scene) {
    this.entries.delete(scene);
    this.indices.delete(scene);
    this.gamepadLatch.delete(scene);
  }

  private install(scene: Phaser.Scene) {
    this.installed.add(scene);
    scene.input.keyboard?.on('keydown-TAB', (event: KeyboardEvent) => {
      event.preventDefault();
      this.move(scene, event.shiftKey ? -1 : 1);
    });
    scene.input.keyboard?.on('keydown-ENTER', () => this.activate(scene));
    scene.input.keyboard?.on('keydown-SPACE', (event: KeyboardEvent) => {
      event.preventDefault();
      this.activate(scene);
    });
    scene.events.on(Phaser.Scenes.Events.UPDATE, () => this.pollGamepad(scene));
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.clear(scene));
  }

  private activeEntries(scene: Phaser.Scene) {
    const active = (this.entries.get(scene) ?? []).filter((entry) => entry.isActive());
    this.entries.set(scene, active);
    const current = this.indices.get(scene) ?? 0;
    if (active.length && current >= active.length) this.indices.set(scene, active.length - 1);
    return active;
  }

  private move(scene: Phaser.Scene, delta: number) {
    const list = this.activeEntries(scene);
    if (!list.length) return;
    const current = this.indices.get(scene) ?? 0;
    this.focus(scene, (current + delta + list.length) % list.length);
  }

  private focus(scene: Phaser.Scene, index: number) {
    const list = this.activeEntries(scene);
    if (!list.length) return;
    list.forEach((entry, entryIndex) => entry.setFocused(entryIndex === index));
    this.indices.set(scene, index);
  }

  private activate(scene: Phaser.Scene) {
    const list = this.activeEntries(scene);
    const index = this.indices.get(scene) ?? 0;
    list[index]?.activate();
  }

  private pollGamepad(scene: Phaser.Scene) {
    const pad = scene.input.gamepad?.getPad(0);
    if (!pad) return;
    const previous = Boolean(pad.buttons[12]?.pressed || pad.leftStick.y < -0.55);
    const next = Boolean(pad.buttons[13]?.pressed || pad.leftStick.y > 0.55);
    const activate = Boolean(pad.buttons[0]?.pressed);
    const pressed = previous || next || activate;
    const latched = this.gamepadLatch.get(scene) ?? false;
    if (pressed && !latched) {
      if (previous) this.move(scene, -1);
      else if (next) this.move(scene, 1);
      else this.activate(scene);
    }
    this.gamepadLatch.set(scene, pressed);
  }
}
